/**
 * Purchase Manager Analytics - Core Interfaces
 *
 * Complete TypeScript type definitions for all analytics events,
 * storage schemas, sync payloads, and funnel tracking.
 */

// ─────────────────────────────────────────────────────────────
// Enums
// ─────────────────────────────────────────────────────────────

export enum SupplierType {
    Hyperpure = 'hyperpure',
    Udaan = 'udaan',
    Dmart = 'dmart',
    Local = 'local',
}

export enum AnalyticsEventType {
    // Search events
    Search = 'search',
    SearchResultView = 'search_result_view',
    SearchItemSelect = 'search_item_select',

    // Cart events
    CartAdd = 'cart_add',
    CartRemove = 'cart_remove',
    CartQuantityChange = 'cart_quantity_change',
    CartAbandon = 'cart_abandon',

    // Checkout events
    CartCheckoutClick = 'cart_checkout_click',
    ProceedToBuy = 'proceed_to_buy',
    ProceedToBuySuccess = 'proceed_to_buy_success',
    ProceedToBuyFailure = 'proceed_to_buy_failure',

    // Local Supplier PO events
    CreatePOOpen = 'create_po_open',
    POFieldInteraction = 'po_field_interaction',
    POQuantityEdit = 'po_quantity_edit',
    POItemRemove = 'po_item_remove',
    GeneratePOClick = 'generate_po_click',
    POSuccess = 'po_success',
    POFailure = 'po_failure',

    // Session events
    SessionStart = 'session_start',
    SessionEnd = 'session_end',
    PageView = 'page_view',

    // Quick-add events
    QuickAddCategoryView = 'quick_add_category_view',
    QuickAddItemSelect = 'quick_add_item_select',

    // Comparison events
    PriceComparisonView = 'price_comparison_view',
    BestPriceSelect = 'best_price_select',

    // Favorite events
    FavoriteAdd = 'favorite_add',
    FavoriteRemove = 'favorite_remove',
}

export enum CheckoutMethod {
    Iframe = 'iframe',          // HP
    Redirect = 'redirect',     // Udaan, Dmart
    InternalPO = 'internal_po', // Local Supplier
}

export enum SyncStatus {
    Pending = 'pending',
    Syncing = 'syncing',
    Synced = 'synced',
    Failed = 'failed',
}

export enum FunnelStage {
    Landing = 'landing',
    Search = 'search',
    Results = 'results',
    CartAdd = 'cart_add',
    CartView = 'cart_view',
    Checkout = 'checkout',
    ProceedToBuy = 'proceed_to_buy',
    CreatePO = 'create_po',
    GeneratePO = 'generate_po',
    Complete = 'complete',
}

// ─────────────────────────────────────────────────────────────
// Base Event
// ─────────────────────────────────────────────────────────────

export interface AnalyticsEventBase {
    /** Unique event ID (UUID v4) */
    eventId: string;
    /** Event type discriminator */
    eventType: AnalyticsEventType;
    /** ISO 8601 timestamp */
    timestamp: string;
    /** Session within which this event occurred */
    sessionId: string;
    /** Logged-in user ID */
    userId: string;
    /** Logged-in user name */
    userName: string;
    /** Logged-in user email */
    userEmail: string;
    /** Selected entity ID */
    entityId: string;
    /** Company (parent) ID */
    companyId: string;
    /** Company (parent) name */
    companyName: string;
    /** Supplier context */
    supplier: SupplierType | string;
    /** Sync status */
    syncStatus: SyncStatus;
    /** Additional metadata */
    meta?: Record<string, any>;
}

// ─────────────────────────────────────────────────────────────
// 1. Search Analytics
// ─────────────────────────────────────────────────────────────

export interface SearchEvent extends AnalyticsEventBase {
    eventType: AnalyticsEventType.Search;
    data: {
        /** The keyword(s) searched */
        keyword: string;
        /** Cumulative search count in this session */
        searchSequenceNumber: number;
        /** Number of results returned */
        resultCount: number;
        /** Breakdown per supplier */
        resultCountBySupplier: Record<string, number>;
        /** Source: typed / recent / most_purchased / top_ordered / most_searched */
        searchSource: 'typed' | 'recent' | 'most_purchased' | 'top_ordered' | 'most_searched' | 'quick_add';
        /** Time (ms) the search API took to respond */
        responseTimeMs: number;
        /** Whether at least one result was returned */
        hasResults: boolean;
        /** Whether the search was a repeat of a previous keyword in this session */
        isRepeatSearch: boolean;
        /** Previous search keyword (for search-chain analysis) */
        previousKeyword?: string;
        /** Time (ms) since previous search in session */
        timeSinceLastSearchMs?: number;
    };
}

export interface SearchResultViewEvent extends AnalyticsEventBase {
    eventType: AnalyticsEventType.SearchResultView;
    data: {
        keyword: string;
        /** How long (ms) the user viewed results before next action */
        viewDurationMs: number;
        /** Number of items visible in viewport */
        visibleItemCount: number;
        /** Whether user scrolled through results */
        didScroll: boolean;
        /** Total result count across all suppliers */
        totalResultCount: number;
    };
}

export interface SearchItemSelectEvent extends AnalyticsEventBase {
    eventType: AnalyticsEventType.SearchItemSelect;
    data: {
        keyword: string;
        /** Selected item name */
        itemName: string;
        /** Selected item SKU ID */
        itemId: string;
        /** Item price at time of selection */
        itemPrice: number;
        /** Item unit */
        itemUnit: string;
        /** Position of selected item in results (0-based) */
        resultPosition: number;
        /** Was this the market-best-price item? */
        isBestPrice: boolean;
        /** Time (ms) from search completion to item selection */
        timeToSelectMs: number;
        /** Whether item was added to cart after selection */
        addedToCart: boolean;
    };
}

// ─────────────────────────────────────────────────────────────
// 2. Cart Interaction Analytics
// ─────────────────────────────────────────────────────────────

export interface CartAddEvent extends AnalyticsEventBase {
    eventType: AnalyticsEventType.CartAdd;
    data: {
        itemId: string;
        itemName: string;
        quantity: number;
        unitPrice: number;
        itemUnit: string;
        /** Total price for this line item */
        lineTotal: number;
        /** Total cart value after this action */
        cartValueAfter: number;
        /** Total items in cart after this action */
        cartItemCountAfter: number;
        /** Time (ms) since session start */
        timeSinceSessionStartMs: number;
        /** Time (ms) since last cart action */
        timeSinceLastCartActionMs?: number;
        /** Whether item was added from best-price section */
        fromBestPrice: boolean;
        /** Whether bulk pricing was applied */
        hasBulkPricing: boolean;
        /** Applied bulk price tier (if any) */
        bulkPriceTier?: { qty: number; price: number };
        /** Search keyword that led to this item */
        originKeyword?: string;
    };
}

export interface CartRemoveEvent extends AnalyticsEventBase {
    eventType: AnalyticsEventType.CartRemove;
    data: {
        itemId: string;
        itemName: string;
        /** Quantity at time of removal */
        quantityRemoved: number;
        unitPrice: number;
        /** Value lost from cart */
        valueLost: number;
        cartValueAfter: number;
        cartItemCountAfter: number;
        /** Duration (ms) item was in cart before removal */
        timeInCartMs: number;
        /** Reason if detectable: 'user_action' | 'out_of_stock' | 'quantity_validation' */
        removalReason: 'user_action' | 'out_of_stock' | 'quantity_validation' | 'unknown';
    };
}

export interface CartQuantityChangeEvent extends AnalyticsEventBase {
    eventType: AnalyticsEventType.CartQuantityChange;
    data: {
        itemId: string;
        itemName: string;
        previousQuantity: number;
        newQuantity: number;
        /** 'increase' | 'decrease' */
        direction: 'increase' | 'decrease';
        unitPrice: number;
        /** Price diff caused by this change */
        priceDelta: number;
        cartValueAfter: number;
        /** Whether this change crossed a bulk-price threshold */
        crossedBulkThreshold: boolean;
        /** New effective price after bulk tier change */
        effectivePrice?: number;
        timeSinceLastCartActionMs?: number;
    };
}

export interface CartAbandonEvent extends AnalyticsEventBase {
    eventType: AnalyticsEventType.CartAbandon;
    data: {
        /** Items left behind */
        abandonedItems: Array<{
            itemId: string;
            itemName: string;
            quantity: number;
            unitPrice: number;
            lineTotal: number;
        }>;
        totalAbandonedValue: number;
        totalAbandonedItems: number;
        /** Time (ms) since last interaction */
        idleDurationMs: number;
        /** Session duration at point of abandon */
        sessionDurationMs: number;
        /** Detected reason: 'navigation' | 'timeout' | 'browser_close' | 'unknown' */
        abandonReason: 'navigation' | 'timeout' | 'browser_close' | 'unknown';
    };
}

// ─────────────────────────────────────────────────────────────
// 3. Cart Checkout Click Analytics
// ─────────────────────────────────────────────────────────────

export interface CartCheckoutClickEvent extends AnalyticsEventBase {
    eventType: AnalyticsEventType.CartCheckoutClick;
    data: {
        totalCartValue: number;
        totalItems: number;
        uniqueItemCount: number;
        /** Per-supplier breakdown */
        supplierBreakdown: Array<{
            supplier: string;
            itemCount: number;
            cartValue: number;
        }>;
        /** Time (ms) from first cart add to checkout click */
        timeFromFirstAddMs: number;
        sessionDurationMs: number;
        /** Number of cart modifications (add/remove/qty change) */
        cartModificationCount: number;
        /** Number of distinct search queries in this session */
        searchCount: number;
        /** Average item price */
        averageItemPrice: number;
        /** Highest single-item value in cart */
        maxItemValue: number;
    };
}

// ─────────────────────────────────────────────────────────────
// 4. Proceed to Buy Analytics
// ─────────────────────────────────────────────────────────────

export interface ProceedToBuyEvent extends AnalyticsEventBase {
    eventType: AnalyticsEventType.ProceedToBuy;
    data: {
        finalItems: Array<{
            itemId: string;
            itemName: string;
            quantity: number;
            unitPrice: number;
            lineTotal: number;
        }>;
        finalTotalValue: number;
        finalItemCount: number;
        /** How checkout is handled for this supplier */
        checkoutMethod: CheckoutMethod;
        /** HP iframe URL / Udaan redirect URL / Dmart redirect URL */
        targetUrl?: string;
        /** Time (ms) from cart drawer open to proceed-to-buy click */
        timeFromCartOpenMs: number;
        sessionDurationMs: number;
        /** Number of items changed between checkout click and proceed */
        itemsModifiedAfterCheckout: number;
        /** Whether user reviewed cart before proceeding */
        cartReviewed: boolean;
    };
}

export interface ProceedToBuyResultEvent extends AnalyticsEventBase {
    eventType:
    | AnalyticsEventType.ProceedToBuySuccess
    | AnalyticsEventType.ProceedToBuyFailure;
    data: {
        checkoutMethod: CheckoutMethod;
        /** Time (ms) from proceed click to success/failure */
        responseTimeMs: number;
        /** Failure details (if applicable) */
        errorMessage?: string;
        errorCode?: string;
        /** Number of items that failed to sync (partial failure) */
        failedItemCount?: number;
        /** Total items attempted */
        totalItemCount: number;
        totalValue: number;
    };
}

// ─────────────────────────────────────────────────────────────
// 5. Local Supplier PO Flow Analytics
// ─────────────────────────────────────────────────────────────

export interface CreatePOOpenEvent extends AnalyticsEventBase {
    eventType: AnalyticsEventType.CreatePOOpen;
    data: {
        /** Local supplier ID */
        localSupplierId: string;
        localSupplierName: string;
        /** Number of items being carried into PO */
        itemCount: number;
        /** Total value of items at PO open */
        initialPOValue: number;
        /** Time (ms) from cart checkout to PO open */
        timeFromCheckoutMs: number;
    };
}

export interface POFieldInteractionEvent extends AnalyticsEventBase {
    eventType: AnalyticsEventType.POFieldInteraction;
    data: {
        /** Field name user interacted with */
        fieldName: string;
        /** 'focus' | 'change' | 'blur' */
        interactionType: 'focus' | 'change' | 'blur';
        /** Whether the field had a validation error */
        hasValidationError: boolean;
        /** Error message (if applicable) */
        validationMessage?: string;
        /** Time (ms) spent on this field */
        fieldDurationMs?: number;
    };
}

export interface POQuantityEditEvent extends AnalyticsEventBase {
    eventType: AnalyticsEventType.POQuantityEdit;
    data: {
        itemId: string;
        itemName: string;
        previousQuantity: number;
        newQuantity: number;
        direction: 'increase' | 'decrease';
        unitPrice: number;
        /** Revised PO total after this edit */
        revisedPOTotal: number;
        /** Count of quantity edits in this PO session */
        editSequenceNumber: number;
    };
}

export interface POItemRemoveEvent extends AnalyticsEventBase {
    eventType: AnalyticsEventType.POItemRemove;
    data: {
        itemId: string;
        itemName: string;
        quantity: number;
        unitPrice: number;
        lineTotal: number;
        revisedPOTotal: number;
        /** Remaining item count */
        remainingItemCount: number;
    };
}

export interface GeneratePOClickEvent extends AnalyticsEventBase {
    eventType: AnalyticsEventType.GeneratePOClick;
    data: {
        localSupplierId: string;
        localSupplierName: string;
        finalItems: Array<{
            itemId: string;
            itemName: string;
            quantity: number;
            unitPrice: number;
            lineTotal: number;
        }>;
        finalPOValue: number;
        finalItemCount: number;
        /** Number of modifications made in PO form */
        totalModifications: number;
        /** Time (ms) from PO open to Generate PO click */
        timeInPOFormMs: number;
        /** Full funnel time: search → generate PO */
        fullFunnelTimeMs: number;
    };
}

export interface POResultEvent extends AnalyticsEventBase {
    eventType: AnalyticsEventType.POSuccess | AnalyticsEventType.POFailure;
    data: {
        localSupplierId: string;
        localSupplierName: string;
        poValue: number;
        itemCount: number;
        /** API response time */
        responseTimeMs: number;
        /** Error message (if failure) */
        errorMessage?: string;
        /** Full funnel time including API response */
        totalFunnelTimeMs: number;
        /** PO number (if success) */
        poNumber?: string;
    };
}

// ─────────────────────────────────────────────────────────────
// Session & Page Events
// ─────────────────────────────────────────────────────────────

export interface SessionEvent extends AnalyticsEventBase {
    eventType: AnalyticsEventType.SessionStart | AnalyticsEventType.SessionEnd;
    data: {
        /** Start or end */
        action: 'start' | 'end';
        /** Duration (only on end) */
        durationMs?: number;
        /** Summary counts (only on end) */
        totalSearches?: number;
        totalCartAdds?: number;
        totalCheckouts?: number;
        totalPOsGenerated?: number;
        /** Page views in session */
        pageViewCount?: number;
        /** User agent */
        userAgent: string;
        /** Screen resolution */
        screenResolution: string;
        /** Whether user is on mobile */
        isMobile: boolean;
    };
}

export interface PageViewEvent extends AnalyticsEventBase {
    eventType: AnalyticsEventType.PageView;
    data: {
        pageRoute: string;
        pageName: string;
        /** Time (ms) on previous page */
        previousPageDurationMs?: number;
        previousPageRoute?: string;
        /** Referrer (if applicable) */
        referrer?: string;
    };
}

// ─────────────────────────────────────────────────────────────
// Quick-Add & Comparison Events
// ─────────────────────────────────────────────────────────────

export interface QuickAddCategoryViewEvent extends AnalyticsEventBase {
    eventType: AnalyticsEventType.QuickAddCategoryView;
    data: {
        category: 'most_purchased' | 'recent' | 'last_searched' | 'most_searched' | 'top_ordered';
        itemCount: number;
    };
}

export interface QuickAddItemSelectEvent extends AnalyticsEventBase {
    eventType: AnalyticsEventType.QuickAddItemSelect;
    data: {
        category: string;
        itemName: string;
        itemId?: string;
        position: number;
    };
}

export interface PriceComparisonViewEvent extends AnalyticsEventBase {
    eventType: AnalyticsEventType.PriceComparisonView;
    data: {
        keyword: string;
        suppliersCompared: string[];
        bestPriceSupplier: string;
        bestPrice: number;
        priceRange: { min: number; max: number };
        /** Potential savings if user picks best price */
        maxSavings: number;
        maxSavingsPercent: number;
    };
}

// ─────────────────────────────────────────────────────────────
// Union Type for All Events
// ─────────────────────────────────────────────────────────────

export type AnalyticsEvent =
    | SearchEvent
    | SearchResultViewEvent
    | SearchItemSelectEvent
    | CartAddEvent
    | CartRemoveEvent
    | CartQuantityChangeEvent
    | CartAbandonEvent
    | CartCheckoutClickEvent
    | ProceedToBuyEvent
    | ProceedToBuyResultEvent
    | CreatePOOpenEvent
    | POFieldInteractionEvent
    | POQuantityEditEvent
    | POItemRemoveEvent
    | GeneratePOClickEvent
    | POResultEvent
    | SessionEvent
    | PageViewEvent
    | QuickAddCategoryViewEvent
    | QuickAddItemSelectEvent
    | PriceComparisonViewEvent;

// ─────────────────────────────────────────────────────────────
// Storage Schema
// ─────────────────────────────────────────────────────────────

export interface AnalyticsStorageSchema {
    /** Schema version for migration support */
    version: number;
    /** Current session ID */
    currentSessionId: string;
    /** ISO timestamp of last successful sync */
    lastSyncTimestamp: string;
    /** ISO timestamp of last write to storage */
    lastWriteTimestamp: string;
    /** Number of consecutive sync failures */
    consecutiveSyncFailures: number;
    /** Events waiting to be synced (ring buffer—oldest dropped first) */
    pendingEvents: AnalyticsEvent[];
    /** Events that failed to sync (max retries not exceeded) */
    failedEvents: AnalyticsEvent[];
    /** Running session counters (lightweight) */
    sessionCounters: SessionCounters;
    /** Funnel snapshots per supplier per session */
    funnelSnapshots: Record<string, FunnelSnapshot>;
}

export interface SessionCounters {
    searchCount: number;
    cartAddCount: number;
    cartRemoveCount: number;
    cartModificationCount: number;
    checkoutClickCount: number;
    proceedToBuyCount: number;
    proceedToBuySuccess: number;
    poGeneratedCount: number;
    pageViewCount: number;
    firstCartAddTimestamp?: string;
    lastCartActionTimestamp?: string;
    cartOpenTimestamp?: string;
}

// ─────────────────────────────────────────────────────────────
// Funnel Tracking
// ─────────────────────────────────────────────────────────────

export interface FunnelSnapshot {
    sessionId: string;
    supplier: string;
    /** Ordered stages the user reached */
    stagesReached: FunnelStage[];
    /** Timestamp when each stage was first reached */
    stageTimestamps: Record<string, string>;
    /** Time (ms) between consecutive stages */
    stageDurations: Record<string, number>;
    /** Where the user dropped off (null = completed) */
    dropOffStage: FunnelStage | null;
    /** Total funnel time */
    totalFunnelTimeMs: number;
    /** Was the funnel completed? */
    completed: boolean;
    /** Final value (cart value or PO value) */
    finalValue: number;
    /** Final item count */
    finalItemCount: number;
}

// ─────────────────────────────────────────────────────────────
// Sync Payload
// ─────────────────────────────────────────────────────────────

export interface AnalyticsSyncPayload {
    /** Batch ID for idempotency */
    batchId: string;
    /** ISO timestamp of this sync attempt */
    syncTimestamp: string;
    /** User ID */
    userId: string;
    /** User display name */
    userName: string;
    /** User email */
    userEmail: string;
    /** Entity ID */
    entityId: string;
    /** Company (parent) ID */
    companyId: string;
    /** Company (parent) name */
    companyName: string;
    /** Client-side session ID */
    sessionId: string;
    /** Platform metadata */
    clientInfo: {
        userAgent: string;
        screenResolution: string;
        timezone: string;
        appVersion: string;
    };
    /** Events in this batch */
    events: AnalyticsEvent[];
    /** Funnel snapshots for completed/dropped funnels */
    funnelSnapshots: FunnelSnapshot[];
    /** Aggregate session counters */
    sessionCounters: SessionCounters;
    /** Aggregated search keywords from search events in this batch */
    searchKeywords: Array<{
        keyword: string;
        count: number;
        supplier: string;
        timestamp: string;
        resultCount: number;
        hasResults: boolean;
    }>;
    /** Aggregated cart products from cart_add events in this batch */
    cartProducts: Array<{
        itemId: string;
        itemName: string;
        quantity: number;
        unitPrice: number;
        itemUnit: string;
        supplier: string;
        timestamp: string;
        count: number;
    }>;
}

export interface AnalyticsSyncResponse {
    success: boolean;
    /** Server-assigned batch ID acknowledgment */
    batchId: string;
    /** Number of events processed */
    processedCount: number;
    /** Event IDs that failed server-side */
    failedEventIds?: string[];
    /** Error message */
    error?: string;
}

// ─────────────────────────────────────────────────────────────
// Configuration
// ─────────────────────────────────────────────────────────────

export interface AnalyticsConfig {
    /** localStorage key prefix */
    storageKeyPrefix: string;
    /** Max events to store before oldest are dropped */
    maxPendingEvents: number;
    /** Max failed events to retain */
    maxFailedEvents: number;
    /** Sync interval in milliseconds (default: 300000 = 5 min) */
    syncIntervalMs: number;
    /** Max consecutive sync failures before back-off */
    maxConsecutiveFailures: number;
    /** Back-off multiplier after failures */
    backoffMultiplier: number;
    /** Max storage size in bytes (default: 2MB) */
    maxStorageSizeBytes: number;
    /** Max retry for failed events */
    maxRetryCount: number;
    /** Whether to log events to console in dev mode */
    debugMode: boolean;
    /** Back-end sync endpoint */
    syncEndpoint: string;
    /** Batch size per sync */
    batchSize: number;
    /** Idle timeout (ms) before cart-abandon event is fired */
    cartAbandonTimeoutMs: number;
}
